import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { QuestionView, QuestionType } from '@formrails/form-builder/data-access';
import { TextboxQuestionComponent } from './textbox-question/textbox-question.component';
import { CheckboxQuestionComponent } from './checkbox-question/checkbox-question.component';
import { DropdownQuestionComponent } from './dropdown-question/dropdown-question.component';
import { EmailQuestionComponent } from './email-question/email-question.component';
import { DateQuestionComponent } from './date-question/date-question.component';
import { PhoneQuestionComponent } from './phone-question/phone-question.component';
import { RadioQuestionComponent } from './radio-question/radio-question.component';
import { TextareaQuestionComponent } from './textarea-question/textarea-question.component';
import { ToggleQuestionComponent } from './toggle-question/toggle-question.component';
import { ConfirmationQuestionComponent } from './confirmation-question/confirmation-question.component';
import { HelperTextQuestionComponent } from './helper-text-question/helper-text-question.component';
import { SectionQuestionComponent } from './section-question/section-question.component';
import { TitleQuestionComponent } from './title-question/title-question.component';

/**
 * Container component that dynamically loads the appropriate question component
 * based on the question type. This follows the Strategy pattern.
 * Uses Angular signals for inputs and outputs.
 */
@Component({
  selector: 'app-question-container',
  standalone: true,
  imports: [
    TextboxQuestionComponent,
    CheckboxQuestionComponent,
    DropdownQuestionComponent,
    EmailQuestionComponent,
    DateQuestionComponent,
    PhoneQuestionComponent,
    RadioQuestionComponent,
    TextareaQuestionComponent,
    ToggleQuestionComponent,
    ConfirmationQuestionComponent,
    HelperTextQuestionComponent,
    SectionQuestionComponent,
    TitleQuestionComponent
  ],
  template: `
    @switch (question().questionType) {
      @case (questionTypes.TextBox) {
        <app-textbox-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-textbox-question>
      }

      @case (questionTypes.CheckboxList) {
        <app-checkbox-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-checkbox-question>
      }

      @case (questionTypes.DropdownList) {
        <app-dropdown-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-dropdown-question>
      }

      @case (questionTypes.DynamicDropdown) {
        <!-- DynamicDropdown uses the same component as DropdownList with hasDynamicOptions flag -->
        <app-dropdown-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-dropdown-question>
      }

      @case (questionTypes.Email) {
        <app-email-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-email-question>
      }

      @case (questionTypes.Date) {
        <app-date-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-date-question>
      }

      @case (questionTypes.Tel) {
        <app-phone-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-phone-question>
      }

      @case (questionTypes.Radio) {
        <app-radio-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-radio-question>
      }

      @case (questionTypes.TextArea) {
        <app-textarea-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-textarea-question>
      }

      @case (questionTypes.Toggle) {
        <app-toggle-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-toggle-question>
      }

      @case (questionTypes.Confirmation) {
        <app-confirmation-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-confirmation-question>
      }

      @case (questionTypes.HelperText) {
        <app-helper-text-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-helper-text-question>
      }

      @case (questionTypes.Section) {
        <app-section-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-section-question>
      }

      @case (questionTypes.Title) {
        <app-title-question
          [question]="question()"
          [isEditing]="isEditing()"
          [disabled]="disabled()"
          (questionChange)="onQuestionChange($event)"
          (configChange)="onConfigChange($event)"
          (delete)="onDelete()">
        </app-title-question>
      }

      @default {
        <div class="question-placeholder">
          <p>Unknown question type: {{ question().questionType }}</p>
        </div>
      }
    }
  `,
  styles: [`
    .question-placeholder {
      padding: 1rem;
      background: var(--color-gray-100);
      border-radius: 0.5rem;
      color: var(--color-gray-600);
      font-family: var(--font-family-base);
      font-size: var(--font-size-sm);
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionContainerComponent {
  readonly question = input.required<QuestionView>();
  readonly isEditing = input(false);
  readonly disabled = input(false);

  readonly questionChange = output<QuestionView>();
  readonly configChange = output<Partial<QuestionView>>();
  readonly delete = output<void>();

  // Expose QuestionType enum to template
  questionTypes = QuestionType;

  onQuestionChange(updatedQuestion: QuestionView): void {
    this.questionChange.emit(updatedQuestion);
  }

  onConfigChange(config: Partial<QuestionView>): void {
    this.configChange.emit(config);
  }

  onDelete(): void {
    this.delete.emit();
  }
}

